import React, { useState, useEffect, useCallback } from 'react';
import { maintenanceApi, formatApiError } from '../../services/api';
import { Plus, X, Trash, PencilSimple, Wrench, CalendarBlank, WarningCircle, Gauge, ClockCounterClockwise, DownloadSimple } from '@phosphor-icons/react';

const fmtKm = (n) => new Intl.NumberFormat('es-MX').format(n || 0);
const fmtMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const INITIAL_FORM = {
  vehicle_id: '',
  type: 'menor',
  date: new Date().toISOString().split('T')[0],
  km_at_service: '',
  cost: '',
  notes: '',
  next_date: '',
};

const MaintenanceTab = ({ vehicles }) => {
  const [status, setStatus] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [filterVehicleId, setFilterVehicleId] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [historyModalVehicle, setHistoryModalVehicle] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [st, hist] = await Promise.all([
        maintenanceApi.status(),
        maintenanceApi.getAll(),
      ]);
      setStatus(st.data);
      setHistory(hist.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModalForVehicle = (vehicleId, type = 'menor') => {
    setEditingId(null);
    setForm({ ...INITIAL_FORM, vehicle_id: vehicleId || '', type });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (record) => {
    setEditingId(record.id);
    setForm({
      vehicle_id: record.vehicle_id || '',
      type: record.type || 'menor',
      date: (record.date || '').substring(0, 10),
      km_at_service: record.km_at_service ? String(record.km_at_service) : '',
      cost: record.cost ? String(record.cost) : '',
      notes: record.notes || '',
      next_date: (record.next_date || '').substring(0, 10) || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.vehicle_id) return setFormError('Selecciona un vehículo');
    if (!form.date) return setFormError('Selecciona una fecha');
    if (!['menor', 'mayor'].includes(form.type)) return setFormError('Tipo inválido');
    try {
      setSaving(true);
      const payload = {
        ...form,
        km_at_service: form.km_at_service ? parseInt(form.km_at_service, 10) : 0,
        cost: form.cost ? parseFloat(form.cost) : 0,
        next_km: null,
        next_date: form.next_date || null,
      };
      if (editingId) {
        await maintenanceApi.update(editingId, payload);
      } else {
        await maintenanceApi.create(payload);
      }
      setShowModal(false);
      setEditingId(null);
      setForm(INITIAL_FORM);
      await load();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este mantenimiento?')) return;
    try {
      await maintenanceApi.delete(id);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const renderNextBadge = (nxt) => {
    if (!nxt) return <span className="text-xs text-[#94A3B8]">—</span>;
    const overdue = nxt.overdue;
    const soon = !overdue && nxt.days_remaining !== undefined && nxt.days_remaining < 30;
    // "Al día" ya no se pinta como cuadro verde: si no hay urgencia, no mostramos badge.
    if (!overdue && !soon) {
      return nxt.next_date
        ? <span className="text-[11px] text-[#64748B]">{fmtDate(nxt.next_date)}</span>
        : <span className="text-xs text-[#94A3B8]">—</span>;
    }
    const bg = overdue ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200';
    return (
      <div className={`inline-flex flex-col gap-0.5 px-2.5 py-1.5 rounded-lg border text-xs ${bg}`}>
        <div className="flex items-center gap-1 font-semibold">
          <WarningCircle size={12} weight="fill" />
          {overdue ? 'Vencido' : 'Próximo'}
        </div>
        {nxt.next_date && <div className="text-[10px] opacity-90">{fmtDate(nxt.next_date)}</div>}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const filteredHistory = history.filter((h) => {
    if (filterVehicleId && h.vehicle_id !== filterVehicleId) return false;
    const d = (h.date || '').substring(0, 10);
    if (dateFrom && d && d < dateFrom) return false;
    if (dateTo && d && d > dateTo) return false;
    return true;
  });

  return (
    <div data-testid="maintenance-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-[#0F172A]">Bitácora de Mantenimientos</h2>
          <p className="text-xs text-[#64748B]">Registra mantenimientos mayor/menor y su próxima fecha de servicio</p>
        </div>
        <button
          onClick={() => openModalForVehicle('')}
          className="btn-primary flex items-center gap-2"
          data-testid="add-maintenance-btn"
        >
          <Plus size={18} /> Registrar Mantenimiento
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}

      {/* Cards grid: status por vehículo */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {status.map((v) => (
          <div key={v.vehicle_id} className={`card p-4 ${v.alert_km ? 'border-2 border-red-400 bg-red-50/40' : ''}`} data-testid={`maintenance-card-${v.vehicle_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-heading font-bold text-[#0F172A] text-sm">
                  {v.vehicle_name || 'Sin nombre'}
                </h3>
                <div className="text-xs text-[#64748B] mt-0.5">
                  {v.vehicle_plates || '—'} · {v.vehicle_year || '—'}
                </div>
                {v.client_name && (
                  <div className="text-xs text-[#0055FF] font-medium mt-0.5 truncate" title={v.client_name}>
                    Cliente: {v.client_name}
                  </div>
                )}
                {v.alert_km && (
                  <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200" data-testid={`alert-maint-km-${v.vehicle_id}`}>
                    <WarningCircle size={11} weight="fill" /> +10,000 km sin mant. ({fmtKm(v.km_since_last_service)})
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-xs text-[#94A3B8]">Km actual</div>
                <div className="font-bold text-[#0055FF] text-sm inline-flex items-center gap-1">
                  <Gauge size={14} />
                  {fmtKm(v.current_km)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="border border-[#E2E8F0] rounded-lg p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Menor</span>
                  {renderNextBadge(v.next_menor)}
                </div>
                {v.last_menor ? (
                  <div className="text-xs text-[#0F172A]">
                    Último: {fmtDate(v.last_menor.date)} · {fmtKm(v.last_menor.km_at_service)} km
                    {v.last_menor.cost ? <span className="text-[#64748B]"> · {fmtMoney(v.last_menor.cost)}</span> : ''}
                  </div>
                ) : (
                  <div className="text-xs text-[#94A3B8]">Sin registros</div>
                )}
              </div>

              <div className="border border-[#E2E8F0] rounded-lg p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Mayor</span>
                  {renderNextBadge(v.next_mayor)}
                </div>
                {v.last_mayor ? (
                  <div className="text-xs text-[#0F172A]">
                    Último: {fmtDate(v.last_mayor.date)} · {fmtKm(v.last_mayor.km_at_service)} km
                    {v.last_mayor.cost ? <span className="text-[#64748B]"> · {fmtMoney(v.last_mayor.cost)}</span> : ''}
                  </div>
                ) : (
                  <div className="text-xs text-[#94A3B8]">Sin registros</div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <button
                onClick={() => openModalForVehicle(v.vehicle_id, 'menor')}
                className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF] font-semibold"
                data-testid={`quick-menor-${v.vehicle_id}`}
              >
                + Menor
              </button>
              <button
                onClick={() => openModalForVehicle(v.vehicle_id, 'mayor')}
                className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F3FF] font-semibold"
                data-testid={`quick-mayor-${v.vehicle_id}`}
              >
                + Mayor
              </button>
              <button
                onClick={() => setHistoryModalVehicle(v)}
                className="text-xs px-2 py-1.5 rounded-lg border border-[#CBD5E1] text-[#64748B] hover:bg-[#F1F5F9] inline-flex items-center gap-1"
                title="Ver historial de este vehículo"
                data-testid={`view-history-${v.vehicle_id}`}
              >
                <ClockCounterClockwise size={13} /> Ver
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* History table */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
            <Wrench size={16} className="text-[#7C3AED]" /> Historial de Mantenimientos
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filterVehicleId}
              onChange={(e) => setFilterVehicleId(e.target.value)}
              className="input py-1 text-sm"
            >
              <option value="">Todos los vehículos</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.brand} {v.sub_brand} — {v.plates}</option>
              ))}
            </select>
            <div className="flex items-center gap-1 text-xs text-[#64748B]">
              <span>Desde</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input py-1 text-xs"
                data-testid="maint-filter-from"
              />
              <span>Hasta</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input py-1 text-xs"
                data-testid="maint-filter-to"
              />
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="ml-1 px-2 py-1 rounded border border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]"
                  data-testid="maint-filter-clear"
                >
                  Limpiar
                </button>
              )}
            </div>
            <a
              href={maintenanceApi.exportExcelUrl(filterVehicleId || null, dateFrom || null, dateTo || null)}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
              title="Descargar Excel según filtros aplicados"
              data-testid="export-maintenance-excel"
            >
              <DownloadSimple size={13} weight="bold" /> Excel
            </a>
          </div>
        </div>
        {filteredHistory.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">Sin registros.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[#E2E8F0]">
                <tr className="text-left text-xs uppercase tracking-wider text-[#64748B]">
                  <th className="py-2">Fecha</th>
                  <th className="py-2">Vehículo</th>
                  <th className="py-2">Placas</th>
                  <th className="py-2">Cliente</th>
                  <th className="py-2">Tipo</th>
                  <th className="py-2 text-right">Km</th>
                  <th className="py-2 text-right">Δ Km</th>
                  <th className="py-2 text-right">Costo</th>
                  <th className="py-2">Próximo</th>
                  <th className="py-2">Notas</th>
                  <th className="py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((h) => (
                  <tr key={h.id} className={`border-b border-[#F1F5F9] hover:bg-[#F8FAFC] ${h.alert_km ? 'bg-red-50/60' : ''}`} data-testid={h.alert_km ? `maint-alert-row-${h.id}` : undefined}>
                    <td className="py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-[#64748B]">
                        <CalendarBlank size={13} /> {fmtDate(h.date)}
                      </span>
                    </td>
                    <td className="py-2">{h.vehicle_name || '—'}</td>
                    <td className="py-2 text-[#64748B]">{h.vehicle_plates || '—'}</td>
                    <td className="py-2 text-[#0F172A] text-xs">{h.client_name || <span className="text-[#CBD5E1]">—</span>}</td>
                    <td className="py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${h.type === 'mayor' ? 'bg-[#F5F3FF] text-[#7C3AED]' : 'bg-[#EFF6FF] text-[#0055FF]'}`}>
                        {h.type === 'mayor' ? 'Mayor' : 'Menor'}
                      </span>
                    </td>
                    <td className="py-2 text-right">{fmtKm(h.km_at_service)}</td>
                    <td className="py-2 text-right text-xs">
                      {h.km_since_last != null ? (
                        <span className={h.alert_km ? 'inline-flex items-center gap-1 font-bold text-red-700' : 'text-[#64748B]'}>
                          {h.alert_km && <WarningCircle size={12} weight="fill" />}
                          +{fmtKm(h.km_since_last)}
                        </span>
                      ) : (
                        <span className="text-[#CBD5E1]">—</span>
                      )}
                    </td>
                    <td className="py-2 text-right">{h.cost ? fmtMoney(h.cost) : '—'}</td>
                    <td className="py-2 text-xs text-[#64748B]">
                      {h.next_date ? fmtDate(h.next_date) : <span className="text-[#CBD5E1]">—</span>}
                    </td>
                    <td className="py-2 text-[#64748B] max-w-xs truncate">{h.notes || '—'}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => openEditModal(h)}
                          className="p-1.5 text-[#0055FF] hover:bg-[#EFF6FF] rounded"
                          title="Editar"
                          data-testid={`edit-maintenance-${h.id}`}
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(h.id)}
                          className="p-1.5 text-[#E11D48] hover:bg-red-50 rounded"
                          title="Eliminar"
                          data-testid={`delete-maintenance-${h.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
              <h2 className="text-lg font-heading font-bold">
                {editingId ? 'Editar Mantenimiento' : 'Registrar Mantenimiento'}
              </h2>
              <button onClick={() => { setShowModal(false); setEditingId(null); }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {formError && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{formError}</div>}
              <div>
                <label className="label">Vehículo *</label>
                <select
                  value={form.vehicle_id}
                  onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
                  className="input"
                  data-testid="maintenance-vehicle-select"
                >
                  <option value="">Selecciona un vehículo</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.brand} {v.sub_brand} — {v.plates} ({v.year})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Tipo *</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="input"
                    data-testid="maintenance-type-select"
                  >
                    <option value="menor">Menor</option>
                    <option value="mayor">Mayor</option>
                  </select>
                </div>
                <div>
                  <label className="label">Fecha *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="input"
                    data-testid="maintenance-date-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Km al servicio</label>
                  <input
                    type="number"
                    value={form.km_at_service}
                    onChange={(e) => setForm({ ...form, km_at_service: e.target.value })}
                    min="0"
                    step="1"
                    className="input"
                    placeholder="Ej: 85000"
                  />
                </div>
                <div>
                  <label className="label">Costo</label>
                  <input
                    type="number"
                    value={form.cost}
                    onChange={(e) => setForm({ ...form, cost: e.target.value })}
                    min="0"
                    step="0.01"
                    className="input"
                    placeholder="Ej: 2500.00"
                  />
                </div>
              </div>
              <div className="border-t border-[#E2E8F0] pt-4">
                <div>
                  <label className="label">Próximo servicio (fecha)</label>
                  <input
                    type="date"
                    value={form.next_date}
                    onChange={(e) => setForm({ ...form, next_date: e.target.value })}
                    className="input"
                    data-testid="maintenance-next-date"
                  />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Opcional: fecha en la que tocará el siguiente servicio.</p>
                </div>
              </div>
              <div>
                <label className="label">Notas</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="input min-h-[70px]"
                  placeholder="Detalle del servicio, taller, refacciones cambiadas…"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowModal(false); setEditingId(null); }} className="btn-secondary flex-1">Cancelar</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="maintenance-save-btn">
                  {saving ? 'Guardando…' : (editingId ? 'Actualizar' : 'Guardar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal Historial del Vehículo */}
      {historyModalVehicle && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setHistoryModalVehicle(null)}
          data-testid="history-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="history-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <div>
                <div className="flex items-center gap-2 text-lg font-heading font-bold text-[#0F172A]">
                  <ClockCounterClockwise size={20} className="text-[#0055FF]" />
                  Historial de Mantenimientos
                </div>
                <div className="text-xs text-[#64748B] mt-0.5">
                  {historyModalVehicle.vehicle_name} · {historyModalVehicle.vehicle_plates} · Km actual {fmtKm(historyModalVehicle.current_km)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setHistoryModalVehicle(null)}
                className="p-2 text-[#64748B] hover:text-[#E11D48] hover:bg-red-50 rounded-lg"
                data-testid="history-modal-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-5">
              {(() => {
                const rows = history.filter((h) => h.vehicle_id === historyModalVehicle.vehicle_id);
                if (rows.length === 0) {
                  return <p className="text-sm text-[#94A3B8] text-center py-8">Sin registros de mantenimiento para este vehículo.</p>;
                }
                return (
                  <table className="w-full text-sm">
                    <thead className="border-b border-[#E2E8F0]">
                      <tr className="text-left text-xs uppercase tracking-wider text-[#64748B]">
                        <th className="py-2">Fecha</th>
                        <th className="py-2">Tipo</th>
                        <th className="py-2 text-right">Km</th>
                        <th className="py-2 text-right">Δ Km</th>
                        <th className="py-2 text-right">Costo</th>
                        <th className="py-2">Próximo</th>
                        <th className="py-2">Notas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((h) => (
                        <tr key={h.id} className={`border-b border-[#F1F5F9] ${h.alert_km ? 'bg-red-50/60' : ''}`}>
                          <td className="py-2 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 text-[#64748B]">
                              <CalendarBlank size={13} /> {fmtDate(h.date)}
                            </span>
                          </td>
                          <td className="py-2">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${h.type === 'mayor' ? 'bg-[#F5F3FF] text-[#7C3AED]' : 'bg-[#EFF6FF] text-[#0055FF]'}`}>
                              {h.type === 'mayor' ? 'Mayor' : 'Menor'}
                            </span>
                          </td>
                          <td className="py-2 text-right">{fmtKm(h.km_at_service)}</td>
                          <td className="py-2 text-right text-xs">
                            {h.km_since_last != null ? (
                              <span className={h.alert_km ? 'inline-flex items-center gap-1 font-bold text-red-700' : 'text-[#64748B]'}>
                                {h.alert_km && <WarningCircle size={12} weight="fill" />}
                                +{fmtKm(h.km_since_last)}
                              </span>
                            ) : (
                              <span className="text-[#CBD5E1]">—</span>
                            )}
                          </td>
                          <td className="py-2 text-right">{h.cost ? fmtMoney(h.cost) : '—'}</td>
                          <td className="py-2 text-xs text-[#64748B]">{h.next_date ? fmtDate(h.next_date) : '—'}</td>
                          <td className="py-2 text-[#64748B] max-w-xs">{h.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
            <div className="px-5 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-2">
              <a
                href={maintenanceApi.exportExcelUrl(historyModalVehicle.vehicle_id)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="history-modal-excel-btn"
              >
                <DownloadSimple size={14} weight="bold" /> Excel
              </a>
              <button
                onClick={() => { setHistoryModalVehicle(null); openModalForVehicle(historyModalVehicle.vehicle_id); }}
                className="btn-primary text-sm"
                data-testid="history-modal-add-btn"
              >
                + Nuevo mantenimiento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceTab;
